import { useRef, useState } from "react";

interface CurvedSliderProps {
  onSpin: (direction: 'clockwise' | 'counterclockwise', spinSpeed: number) => void;
  radius: number;
}

const trackWidth = 40;
const startAngle = 0;
const endAngle = 180;

const degreesToRadians = (deg: number) => (deg * Math.PI) / 180;
const radiansToDegrees = (rad: number) => (rad * 180) / Math.PI

const CurvedSlider: React.FC<CurvedSliderProps> = ({ onSpin, radius }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [angle, setAngle] = useState(0);
  const [rotationDegrees, setRotationDegrees] = useState(0);

  const getAngleFromEvent = (e: React.MouseEvent | React.TouchEvent) => {
    const svg = svgRef.current;
    if (!svg) return 0;

    const rect = svg.getBoundingClientRect();
    const x = "touches" in e ? e.touches[0].clientX : e.clientX;
    const y = "touches" in e ? e.touches[0].clientY : e.clientY;

    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = x - cx;
    const dy = y - cy;

    let theta = radiansToDegrees(Math.atan2(dy, dx));
    if (theta < -180) theta += 360;
    theta = Math.max(startAngle, Math.min(endAngle, theta));
    return theta;
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const theta = getAngleFromEvent(e);
    const direction = theta < angle ? 'counterclockwise' : 'clockwise';
    setAngle(theta);

    // Optional live rotation styling
    const degrees = (theta + 135 - 73) / 300;
    setRotationDegrees(prev => direction === 'clockwise' 
      ? prev + degrees 
      : prev - degrees);
  };

  const handleRelease = () => {
    const direction = angle < 90 ? "counterclockwise" : "clockwise";
    const spinSpeed = Math.abs(angle) < 90 ? 2 : 1;
    onSpin(direction, spinSpeed);
    setAngle(0);
  };

  const generateArcPath = (r: number, thickness: number) => {
    const largeArc = endAngle - startAngle <= 180 ? "0" : "1";
    const start = polarToCartesian(r, startAngle);
    const end = polarToCartesian(r, endAngle);
    return `
      M ${start.x} ${start.y}
      A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}
    `;
  };

  const polarToCartesian = (r: number, angleDeg: number) => {
    const angleRad = degreesToRadians(angleDeg);
    return {
      x: radius + r * Math.cos(angleRad),
      y: radius + r * Math.sin(angleRad),
    };
  };

  return (
    <>
      <style>
        {`
          .wheel-container > div > div {
            transform: rotate(${-73 + rotationDegrees}deg);
            transition: transform 0.1s ease-out;
          }
        `}
      </style>
      <div className="curved-slider">
        <div style={{ width: `${radius * 2}px`, height: `${radius}px`, margin: "0 auto" }}>
          <svg
            ref={svgRef}
            width={radius * 2}
            height={radius + 20}
            viewBox={`0 0 ${radius * 2} ${radius * 2}`}
            onMouseMove={handleMove}
            onMouseUp={handleRelease}
            onTouchMove={handleMove}
            onTouchEnd={handleRelease}
            style={{ touchAction: "none" }}
          >
            <path
              d={generateArcPath(radius - trackWidth / 2, trackWidth)}
              fill="none"
              strokeWidth={trackWidth}
            />
            <circle
              cx={polarToCartesian(radius - trackWidth / 2, angle).x}
              cy={polarToCartesian(radius - trackWidth / 2, angle).y}
              r={40}
              fill="#fff"
            />
          </svg>
        </div>
      </div>
    </>
  );
};

export default CurvedSlider;